import { IStatusPanelAngularComp } from '@ag-grid-community/angular';
import { IStatusPanelParams } from '@ag-grid-community/core/dist/esm/es6/interfaces/iStatusPanel';
import { Component, signal } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { PAGE_SIZES_LIST } from 'shared/constants/general-constants';

@Component({
    selector: 'status-component',
    templateUrl: './custom-pagination.component.html',
    styleUrls: ['./custom-pagination.component.scss'],
    standalone: false,
})
export class CustomPaginationComponent implements IStatusPanelAngularComp {
    private params!: IStatusPanelParams;
    pageSizesList = PAGE_SIZES_LIST;
    fromRow: number = 1;
    toRow!: number;
    totalRowCount = signal<number | undefined>(undefined);
    totalPages!: number;

    pageSizeChanged!: Function;
    pageChanged!: Function;
    pageSizeForm = new UntypedFormGroup({
        size: new UntypedFormControl(PAGE_SIZES_LIST[0], Validators.required),
    });

    currentPageNumber: number = 1;
    private currentPageSize: number = PAGE_SIZES_LIST[0];
    private oldPageSize!: number;

    public agInit(params: any): void {
        this.params = params;
        this.pageSizeChanged = params.pageSizeChanged;
        this.pageChanged = params.pageChanged;
        this.initPagination();
    }

    first() {
        this.setCurrentPage(1);
        this.setFromRow(1);
        this.setToRow(this.currentPageSize);
        this.onPageChanged();
    }

    previous() {
        this.setCurrentPage(this.currentPageNumber - 1);
        this.setFromRow(this.fromRow - this.currentPageSize);
        this.setToRow(this.fromRow + this.currentPageSize - 1);
        this.onPageChanged();
    }

    next() {
        this.setCurrentPage(this.currentPageNumber + 1);
        this.setFromRow(this.fromRow + this.currentPageSize);
        this.setToRow(this.toRow + this.currentPageSize);
        this.onPageChanged();
    }

    last() {
        this.setCurrentPage(this.totalPages);
        this.setFromRow((this.currentPageNumber - 1) * this.currentPageSize + 1);
        this.setToRow(this.totalRowCount());
        this.setCurrentPage(this.totalPages);
        this.onPageChanged();
    }

    private setFromRow(value: number) {
        this.fromRow = value > 1 ? value : 1;
    }

    private setToRow(value: number | undefined) {
        const currentValueInTotalRowCount = this.totalRowCount();
        if (value === undefined || currentValueInTotalRowCount === undefined) {
            return;
        }
        this.toRow = value > currentValueInTotalRowCount ? currentValueInTotalRowCount : value;
    }

    private setCurrentPage(value: number) {
        this.currentPageNumber = value > this.totalPages ? this.totalPages : value;
    }

    private setTotalPages() {
        const currentValueInTotalRowCount = this.totalRowCount();
        if (currentValueInTotalRowCount === undefined) {
            return;
        }
        this.totalPages = Math.ceil(currentValueInTotalRowCount / this.currentPageSize);
        this.totalPages = this.totalPages === 0 ? 1 : this.totalPages;
    }

    private initPagination() {
        this.setCurrentPage(1);
        this.setTotalPages();
        this.setFromRow(1);
        this.setToRow(this.currentPageNumber * this.currentPageSize);
    }

    public onPageSizeChanged() {
        this.oldPageSize = +this.currentPageSize;
        this.currentPageSize = +this.pageSizeForm.value.size;

        const newPageNumber = this.updatePageNumber();
        this.setTotalPages();
        this.setCurrentPage(newPageNumber);
        this.setToRow(newPageNumber * this.currentPageSize);
        this.setFromRow((newPageNumber - 1) * this.currentPageSize + 1);

        this.pageSizeChanged(this.currentPageSize, newPageNumber);
    }

    private onPageChanged() {
        this.pageChanged(this.currentPageSize, this.currentPageNumber);
    }

    private updatePageNumber() {
        // Find out the index of the first row displayed by the page using the old page size
        const index = this.oldPageSize * (this.currentPageNumber - 1) + 1;
        const newPageNumber = index / this.currentPageSize;

        return Math.ceil(newPageNumber);
    }

    private setPageSize(pageSize: number) {
        this.currentPageSize = PAGE_SIZES_LIST[PAGE_SIZES_LIST.indexOf(pageSize)];
        this.pageSizeForm.setValue({ size: pageSize });
    }

    public setRowCount(value: number) {
        this.totalRowCount.set(value);
        this.setToRow(this.totalRowCount() === undefined ? this.currentPageSize : this.currentPageNumber * this.currentPageSize);
        this.setTotalPages();
    }

    public setPageParams(pageNumber: number, pageSize: number) {
        this.setCurrentPage(pageNumber);
        this.setPageSize(pageSize);
        this.setTotalPages();
        this.setFromRow((pageNumber - 1) * pageSize + 1);
        this.setToRow(this.totalRowCount() === undefined ? this.currentPageSize : this.currentPageNumber * this.currentPageSize);
    }
}
